import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  X,
  ChevronDown,
  Zap,
  FlaskConical,
  FileText,
  Plug,
  HeadphonesIcon,
} from "lucide-react";

interface Feature {
  label: string;
  free: string | boolean;
  individual: string | boolean;
  enterprise: string | boolean;
}

interface FeatureGroup {
  title: string;
  icon: React.ReactNode;
  features: Feature[];
}

const featureGroups: FeatureGroup[] = [
  {
    title: "Usage & AI Models",
    icon: <Zap className="w-5 h-5" />,
    features: [
      {
        label: "Volume Limits",
        free: "10 pages / day",
        individual: "100 pages / day",
        enterprise: "Unlimited documents",
      },
      {
        label: "AI Models",
        free: "Standard models",
        individual: "Latest most powerful models",
        enterprise: "Custom / Fine-tuned models",
      },
    ],
  },
  {
    title: "Chemistry Data",
    icon: <FlaskConical className="w-5 h-5" />,
    features: [
      {
        label: "Data Fidelity & DR Molfiles",
        free: "DR molfiles for first 10 molecules/day, then fallback to basic SMILES",
        individual: "Unlimited DR molfiles with full Markush & R-group support",
        enterprise: "Unlimited DR molfiles",
      },
      {
        label: "Combinatorial Enumeration",
        free: 'Teaser: "Markush structure represents X molecules"',
        individual: "Full combinatorial expansion of Markush",
        enterprise: "Automated pipeline integration",
      },
      {
        label: "Molecule Reporting",
        free: "Basic molecule list (Image + SMILES)",
        individual:
          "Full molecule inventory with text co-references & page mapping",
        enterprise:
          "Global molecule inventory with cross-document deduplication",
      },
      {
        label: "Reaction Scheme Mapping",
        free: "Flat list of reactants, conditions, and products",
        individual:
          "Fully mapped graphical reaction schemes showing step-by-step pathways",
        enterprise: "Batch processing for bulk historical reaction mapping",
      },
    ],
  },
  {
    title: "Reporting & Export",
    icon: <FileText className="w-5 h-5" />,
    features: [
      {
        label: "Data Provenance & Audit",
        free: "Standard text and data extraction",
        individual: "Standard text and data extraction",
        enterprise: "Full audit trails for IP and regulatory compliance",
      },
      {
        label: "Export Formats",
        free: "Basic JSON, CSV",
        individual: "JSON, CSV, ORD (coming soon)",
        enterprise: "All formats + Custom data schemas (ORD, ODR, etc.)",
      },
    ],
  },
  {
    title: "Integrations & API",
    icon: <Plug className="w-5 h-5" />,
    features: [
      {
        label: "Ecosystem Integrations",
        free: "Manual download & upload required",
        individual: "1-Click Push integrations (coming soon)",
        enterprise: "Custom M&A & legacy archive integrations",
      },
      {
        label: "API Access",
        free: false,
        individual: false,
        enterprise: true,
      },
    ],
  },
  {
    title: "Support & Security",
    icon: <HeadphonesIcon className="w-5 h-5" />,
    features: [
      {
        label: "Support",
        free: "Basic / Community support",
        individual: "Standard support",
        enterprise:
          "24/7 Priority, Dedicated Account Manager, On-premise options",
      },
    ],
  },
];

const plans = [
  {
    key: "free" as const,
    name: "Free",
    price: "£0",
    period: "/ month",
    buttonText: "Get started",
    buttonLink: "/signup",
    highlighted: false,
  },
  {
    key: "individual" as const,
    name: "Individual/Academics",
    price: "Let's chat",
    // period: "/ month",
    buttonText: "Contact us",
    buttonLink: "/contact",
    highlighted: true,
  },
  {
    key: "enterprise" as const,
    name: "Enterprise",
    price: "Custom",
    period: "",
    buttonText: "Contact us",
    buttonLink: "/contact",
    highlighted: false,
  },
];

const CellValue: React.FC<{ value: string | boolean }> = ({ value }) => {
  if (value === false) {
    return <X className="w-5 h-5 text-gray-600 mx-auto" />;
  }
  if (value === true) {
    return <CheckCircle className="w-5 h-5 text-[#00A3FF] mx-auto" />;
  }
  return <span className="text-gray-300 text-sm leading-snug">{value}</span>;
};

const PricingPlans: React.FC = () => {
  const [openGroups, setOpenGroups] = useState<Set<number>>(
    new Set(featureGroups.map((_, i) => i)),
  );
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(1); // Individual by default

  const toggleGroup = (index: number) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const selectedPlan = plans[selectedPlanIndex]!;

  return (
    <div className="bg-dark-bg min-h-screen">
      {/* Header */}
      <div className="container mx-auto w-full max-w-5xl px-5 md:px-0 pt-20">
        <h1 className="text-5xl md:text-[3.5rem] leading-none font-semibold">
          <span className="bg-gradient-text bg-clip-text text-transparent">
            Pricing Plans
          </span>
        </h1>
        <p className="mt-4 text-white/60 text-lg max-w-xl leading-relaxed">
          Start with our Free tier or unlock advanced features with Individual
          and Enterprise plans.
        </p>
      </div>

      {/* Divider */}
      <div className="container mx-auto w-full max-w-5xl px-5 md:px-0 mt-12">
        <div className="border-t border-white/10" />
      </div>

      <section className="mt-16 pb-16">
        <div className="container mx-auto px-5">
          {/* ── MOBILE LAYOUT ── */}
          <div className="md:hidden">
            {/* Plan tab selector */}
            <div className="flex border border-white/10 rounded-lg p-1 mb-6 gap-1">
              {plans.map((plan, i) => (
                <button
                  key={plan.key}
                  onClick={() => setSelectedPlanIndex(i)}
                  className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                    selectedPlanIndex === i
                      ? plan.highlighted
                        ? "bg-gradient-to-r from-[#00A3FF] to-[#0077CC] text-white"
                        : "bg-white/10 text-white"
                      : "text-white/40 hover:text-white/70"
                  }`}
                >
                  {plan.name}
                </button>
              ))}
            </div>

            {/* Selected plan header card */}
            <div
              className={`rounded-lg p-5 mb-6 border ${
                selectedPlan.highlighted
                  ? "bg-[#00A3FF]/5 border-[#00A3FF]/30"
                  : "bg-white/[0.03] border-white/10"
              }`}
            >
              {selectedPlan.highlighted && (
                <span className="inline-block text-[#00A3FF] text-xs uppercase tracking-widest mb-3">
                  Most popular
                </span>
              )}
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-xl font-semibold text-white">
                    {selectedPlan.name}
                  </div>
                  <div>
                    <span className="text-3xl font-semibold text-white">
                      {selectedPlan.price}
                    </span>
                    {selectedPlan.period && (
                      <span className="text-white/40 text-sm ml-1">
                        {selectedPlan.period}
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to={selectedPlan.buttonLink}
                  className={`font-semibold px-5 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                    selectedPlan.highlighted
                      ? "bg-gradient-to-r from-[#00A3FF] to-[#0077CC] text-white"
                      : "bg-white/[0.06] text-white border border-white/10 hover:bg-white/10"
                  }`}
                >
                  {selectedPlan.buttonText}
                </Link>
              </div>
            </div>

            {/* Feature groups for selected plan */}
            {featureGroups.map((group, groupIndex) => {
              const isOpen = openGroups.has(groupIndex);
              return (
                <div
                  key={groupIndex}
                  className="mb-2 border border-white/10 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-white font-semibold hover:bg-white/[0.03] transition-colors"
                    onClick={() => toggleGroup(groupIndex)}
                  >
                    <span className="text-[#00A3FF]">{group.icon}</span>
                    <span className="text-sm">{group.title}</span>
                    <ChevronDown
                      className={`w-4 h-4 text-white/30 ml-auto transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-white/5">
                      {group.features.map((feature, fi) => (
                        <div key={fi} className="px-4 py-3 bg-white/[0.015]">
                          <div className="text-xs uppercase tracking-widest text-white/30 mb-1.5">
                            {feature.label}
                          </div>
                          <CellValue value={feature[selectedPlan.key]} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── DESKTOP LAYOUT ── */}
          <div className="hidden md:block max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="w-[28%]" />
                  {plans.map((plan) => (
                    <th
                      key={plan.name}
                      className={`px-6 pb-6 text-left w-[24%] ${
                        plan.highlighted
                          ? "bg-[#00A3FF]/5 border-x border-t border-[#00A3FF]/20"
                          : ""
                      }`}
                    >
                      <div className="mt-6 mb-1">
                        {plan.highlighted ? (
                          <p className="text-xs uppercase tracking-widest text-[#00A3FF] mb-2 h-4">
                            Most popular
                          </p>
                        ) : (
                          <div className="h-4 mb-2" />
                        )}
                        <div className="text-lg font-semibold text-white">
                          {plan.name}
                        </div>
                        <div className="mt-1 mb-4">
                          <span className="text-2xl font-semibold text-white">
                            {plan.price}
                          </span>
                          {plan.period && (
                            <span className="text-white/40 text-sm ml-1">
                              {plan.period}
                            </span>
                          )}
                        </div>
                      </div>
                      <Link
                        to={plan.buttonLink}
                        className={`inline-block w-full font-semibold py-2 rounded-lg text-sm text-center transition-all duration-200 ${
                          plan.highlighted
                            ? "bg-gradient-to-r from-[#00A3FF] to-[#0077CC] text-white hover:shadow-lg hover:shadow-[#00A3FF]/25"
                            : "bg-white/[0.06] text-white hover:bg-white/10 border border-white/10"
                        }`}
                      >
                        {plan.buttonText}
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureGroups.map((group, groupIndex) => {
                  const isOpen = openGroups.has(groupIndex);
                  return (
                    <React.Fragment key={groupIndex}>
                      <tr
                        className="border-t border-white/10 cursor-pointer hover:bg-white/[0.02] transition-colors"
                        onClick={() => toggleGroup(groupIndex)}
                      >
                        <td colSpan={4} className="py-4 px-6">
                          <div className="flex items-center gap-3 text-white font-semibold">
                            <span className="text-[#00A3FF]">{group.icon}</span>
                            <span className="text-sm">{group.title}</span>
                            <ChevronDown
                              className={`w-4 h-4 text-white/30 ml-auto transition-transform duration-200 ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </td>
                      </tr>
                      {isOpen &&
                        group.features.map((feature, featureIndex) => (
                          <tr
                            key={featureIndex}
                            className="border-t border-white/5"
                          >
                            <td className="py-4 pl-14 pr-6 text-xs uppercase tracking-widest text-white/30">
                              {feature.label}
                            </td>
                            {(
                              ["free", "individual", "enterprise"] as const
                            ).map((planKey, colIndex) => (
                              <td
                                key={planKey}
                                className={`py-4 px-6 text-center align-middle ${
                                  colIndex === 1
                                    ? "bg-[#00A3FF]/5 border-x border-[#00A3FF]/20"
                                    : ""
                                }`}
                              >
                                <CellValue value={feature[planKey]} />
                              </td>
                            ))}
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
                <tr className="border-t border-white/10">
                  <td className="py-6 px-6" />
                  {plans.map((plan, colIndex) => (
                    <td
                      key={plan.name}
                      className={`py-6 px-6 text-center ${
                        colIndex === 1
                          ? "bg-[#00A3FF]/5 border-x border-b border-[#00A3FF]/20"
                          : ""
                      }`}
                    >
                      <Link
                        to={plan.buttonLink}
                        className={`inline-block w-full font-semibold py-2 rounded-lg text-sm transition-all duration-200 ${
                          plan.highlighted
                            ? "bg-gradient-to-r from-[#00A3FF] to-[#0077CC] text-white hover:shadow-lg hover:shadow-[#00A3FF]/25"
                            : "bg-white/[0.06] text-white hover:bg-white/10 border border-white/10"
                        }`}
                      >
                        {plan.buttonText}
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-5 max-w-5xl">
          <div className="border-t border-white/10 pt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/40 mb-2">
                Enterprise
              </p>
              <h2 className="text-xl font-semibold text-white">
                Need a custom solution?
              </h2>
              <p className="text-white/50 text-sm mt-1 max-w-md">
                We can tailor our services to meet your specific requirements.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-white/[0.06] text-white hover:bg-white/10 border border-white/10 font-semibold px-6 py-3 rounded-lg text-sm transition-all duration-200 shrink-0"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPlans;
